<template>
  <div data-testid="shrinkage-tab">
    <div class="tab-header-row adjustment-header-row">
      <h3>Shrinkage</h3>
      <AlButton
        variant="secondary"
        @click="addNew()"
        :disabled="isNew || !editingAllowed()"
        data-testid="shrinkage-tab-add-new"
        id="shrinkage-tab-add-new"
        :tooltip="
          this.editingAllowed()
            ? ''
            : 'New adjustments cannot be made to months in the past'
        "
      >
        <i class="fa fa-plus button-icon" /> Add</AlButton
      >
    </div>
    <div class="current-percentage">
      Default
      <span class="current-percentage-value"
        >{{ configuration.defaultShrinkageTargetPercentage }}%</span
      >
    </div>
    <div class="shrinkage-rows">
      <ShrinkageTable
        :configuration="configuration"
        :rows="shrinkageRows"
        :isNew="isNew"
        :editingAllowed="editingAllowed()"
        :month="month"
        @cancel="cancel"
        @save="saveRowChange"
      />
    </div>
    <div class="tab-actions">
      <AlButton
        v-if="showUpdateButton"
        :disabled="!hasChanges"
        @click="update"
        data-testid="shrinkage-tab-update"
        id="shrinkage-tab-update"
      >
        <AlLoading v-if="saving" size="sm" />
        <span v-if="!saving">Update</span>
      </AlButton>
    </div>
  </div>
</template>

<script>
import { Al2Button as AlButton, AlLoading } from "@playvox/almendra-components";
import { clone, equals } from "ramda";
import ShrinkageTable from "./ShrinkageTable.vue";
import { detailsPageEventBus } from "../../../../config/eventbus";
import { utcToZonedTime, zonedTimeToUtc } from "date-fns-tz";
import { startOfMonth } from "date-fns";

export default {
  name: "ShrinkageTab",
  components: {
    AlButton,
    ShrinkageTable,
    AlLoading,
  },
  watch: {
    $route() {
      this.isNew = false;
    },
  },
  data() {
    return {
      detailsPageEventBus,
      shrinkageRows: clone(
        this.$route.params.configuration?.shrinkageAdjustments || []
      ),
      configuration: this.$route.params.configuration,
      editingShrinkage: null,
      isNew: false,
      saving: false,
    };
  },
  computed: {
    hasChanges() {
      return !equals(
        this.shrinkageRows,
        this.configuration.shrinkageAdjustments
      );
    },
    showUpdateButton() {
      return (
        this.shrinkageRows.length > 0 ||
        this.$route.params.configuration?.shrinkageAdjustments?.length > 0
      );
    },
    month() {
      return this.$route.params.month;
    },
  },
  methods: {
    editingAllowed() {
      const thisMonthStartInPlanTimezone = zonedTimeToUtc(
        this.month,
        this.configuration.timezone
      );
      //The double conversion is necessary as the browser will return startOfMonth in the browsers timezone
      // e.g. if the browser is in Sydney+10 and the plan is in Hawaii-10
      // new Date()             --> 2022-09-01 19:00:00 GMT+1000      // We're viewing the plan at 7pm September in Sydney
      // utcToZoneTime(Hawaii)  --> 2022-08-31 23:00:00 GMT+1000      // Which is 11pm August in Hawaii
      // startOfMonth           --> 2022-08-01 00:00:00 GMT+1000      // start of that month in browsers timezone
      // zonedTimeToUtc(Hawaii) --> 2022-08-01 20:00:00 GMT+1000      // That month started at 8pm browser time
      // We don't allow editing of past months in the plans timezone
      // So editing is still allowed for another hour in this plans timezone in this example
      const todaysMonthInPlanTimezone = zonedTimeToUtc(
        startOfMonth(utcToZonedTime(new Date(), this.configuration.timezone)),
        this.configuration.timezone
      );

      return thisMonthStartInPlanTimezone >= todaysMonthInPlanTimezone;
    },
    addNew() {
      this.isNew = true;
    },
    cancel() {
      this.isNew = false;
    },
    close() {
      this.editingShrinkage = null;
    },
    saveRowChange(shrinkageRows) {
      this.shrinkageRows = shrinkageRows;
      this.isNew = false;
    },
    async update() {
      this.saving = true;
      // Save new rows
      this.configuration.shrinkageAdjustments = this.shrinkageRows;
      await this.configuration.update();

      //Reload the graph
      this.detailsPageEventBus.$emit(
        "adjustmentChanged",
        this.configuration._id,
        () => {
          this.saving = false;
        }
      );

      this.$emit("change", this.configuration);
    },
  },
};
</script>

<style lang="scss" scoped>
.tab-header-row {
  display: flex;
  align-items: center;

  & > * {
    margin-right: 1rem;
  }
}

.current-percentage {
  padding-bottom: 1rem;
}

.adjustment-header-row h3 {
  min-width: 10em;
}

.button-icon {
  margin-right: 0.5rem;
}

.tab-actions {
  display: flex;
  justify-content: flex-end;
  margin-top: 0.5rem;
}
</style>
